import baseUrl from '@client/util/baseUrl';
// eslint-disable-next-line import/no-cycle
import bffApi, {BffResponse} from '@client/bffApi';
import {AdvisorService, RiaUser} from '@simpleinvest/ms-advisor-sdk';
import {
  CustodianService,
  Disclosures,
  AdditionalInfoHeader,
  AccountAdditionalInfo,
  GetAccountAdditionalInfoLineOptions,
} from '@simpleinvest/ms-custodian-sdk';
import {OrchestrateService} from '@simpleinvest/ms-orchestrate-sdk';
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {ClientService, OnboardingContactInfoOptions} from '@simpleinvest/ms-client-sdk';
import {AxiosResponse} from 'axios';
import {SourcePlatform} from '@simpleinvest/ms-core-sdk';
import {ClientInformationResponse} from './response/ServiceResponse';

export interface UpdateClientEmailOptions {
  clUserId: string;
  email: string;
}

export interface VerifyUserEmailChangeOptions {
  email: string;
  emailVerificationCode: string;
  riaId: string;
}

export interface ResendChangeEmailVerificationCode {
  clUserId: string;
  email: string;
}

export interface GetClientInfoOptions {
  clUserId: string;
}

export default {
  getCustodianServiceInstance(): CustodianService {
    return new CustodianService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getClientServiceInstance(): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getAdvisorServiceInstance(): AdvisorService {
    return new AdvisorService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getOrchestrateServiceInstance(): OrchestrateService {
    return new OrchestrateService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  async getRiaUserFromRiaUserId(riaUserId: string): Promise<RiaUser> {
    const service: AdvisorService = await this.getAdvisorServiceInstance();

    return service.getRiaUserFromRiaUserId({riaUserId});
  },
  async getDisclosures(clUserId: string): Promise<Disclosures> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getClientDisclosures({clUserId});
  },
  async sendEmailToAdvisor({clUserId, riaUserId, template}, fields = {}): Promise<void> {
    const payload = {
      clUserId,
      riaUserId,
      templateName: template,
      overWriteFields: {'login-link': baseUrl.internal(), ...fields},
    };

    const service: OrchestrateService = await this.getOrchestrateServiceInstance();

    return service.sendEmailToAdvisor(payload);
  },

  async saveOnboardingContactInformation(options: OnboardingContactInfoOptions): Promise<void> {
    const service: ClientService = await this.getClientServiceInstance();

    const response = await service.saveOnboardingContactInformation(options);

    return response;
  },
  async updateClientEmail(options: UpdateClientEmailOptions): Promise<void> {
    const url = `${bffApi.getBaseUrl()}/v1/clients/${options.clUserId}/update-email`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await bffApi.getAxiosInstance().post<BffResponse<any>>(url, {
      email: options.email,
    });
  },
  async verifyUserEmailChange(options: VerifyUserEmailChangeOptions): Promise<void> {
    const url = `${bffApi.getBaseUrl()}/v1/rias/${options.riaId}/client/verify-email`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await bffApi.getAxiosInstance().post<BffResponse<any>>(url, {
      email: options.email,
      emailVerificationCode: options.emailVerificationCode,
    });
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getClientInformation(options: GetClientInfoOptions): Promise<AxiosResponse<BffResponse<ClientInformationResponse>>> {
    const url = `${bffApi.getBaseUrl(true)}/clients/${options.clUserId}`;

    return bffApi.getAxiosInstance().get<BffResponse<ClientInformationResponse>>(url);
  },
  async getAccountAdditionalHeadersInfo(): Promise<AdditionalInfoHeader[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAdditionalInfoHeader({});
  },
  async getAccountAdditionalHeaderInfo(options: GetAccountAdditionalInfoLineOptions): Promise<AccountAdditionalInfo[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAccountAdditionalInfoLine(options);
  },
};
